
import React, { useState } from 'react';
import './VoiceQcDashboard.css';
import { VoiceUploader } from './VoiceUploader';
import { VoiceAnalysisPanel } from './VoiceAnalysisPanel';
import { RecordsOverview } from './RecordsOverview';
import { analyzeAudioStream } from '../../services/api';
import { VoiceQcAnalysis } from './types';
import { PulseTag } from '../../components/PulseTag/PulseTag';
import { QCStandardsManager } from '../qcstandards/QCStandardsManager';
import { PromptDisplayPanel } from '../qcstandards/PromptDisplayPanel';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

type VoiceQcDashboardProps = {
  currentView: 'analyze' | 'records';
};

type AnalysisState = {
  transcription?: string;
  detectedLanguage?: string;
  analysis?: VoiceQcAnalysis;
  status: 'idle' | 'transcribing' | 'transcribed' | 'analyzing' | 'completed' | 'error';
  error?: string;
};

interface QCStandard {
  id: number;
  name: string;
  source_file: string | null;
  sheet_name: string | null;
  description: string | null;
  dimensions: any[] | null;
  is_active: boolean;
  created_at: string | null;
}

export const VoiceQcDashboard: React.FC<VoiceQcDashboardProps> = ({ currentView }) => {
  const [analysisState, setAnalysisState] = useState<AnalysisState>({
    status: 'idle',
  });
  const [loading, setLoading] = useState(false);
  const [selectedStandard, setSelectedStandard] = useState<QCStandard | null>(null);

  const handleAnalyze = async (file: File) => {
    setLoading(true);
    setAnalysisState({ status: 'idle' });

    try {
      // 使用带质检标准的API
      const url = selectedStandard 
        ? `${API_BASE_URL}/api/analyze-stream-with-standard?standard_id=${selectedStandard.id}`
        : `${API_BASE_URL}/api/analyze-stream`;
      
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`分析失败: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('无法读取响应流');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (!jsonStr || jsonStr.trim() === '') continue;

            try {
              const data = JSON.parse(jsonStr);

              if (data.error) {
                setAnalysisState({ status: 'error', error: data.error });
                setLoading(false);
                return;
              }

              switch (data.status) {
                case 'transcribing':
                  setAnalysisState({ status: 'transcribing' });
                  break;
                case 'transcribed':
                  setAnalysisState({
                    status: 'transcribed',
                    transcription: data.transcription,
                    detectedLanguage: data.detectedLanguage,
                  });
                  break;
                case 'analyzing':
                  setAnalysisState((prev) => ({ ...prev, status: 'analyzing' }));
                  break;
                case 'completed':
                  setAnalysisState({
                    status: 'completed',
                    transcription: data.analysis.transcription,
                    detectedLanguage: data.analysis.detectedLanguage,
                    analysis: data.analysis,
                  });
                  setLoading(false);
                  break;
              }
            } catch (parseError) {
              console.error('JSON解析失败:', parseError);
            }
          }
        }
      }
    } catch (error) {
      console.error('分析失败:', error);
      setAnalysisState({
        status: 'error',
        error: error instanceof Error ? error.message : '未知错误',
      });
      setLoading(false);
      alert('分析失败，请检查后端服务是否启动');
    }
  };

  return (
    <div className="qc-layout">
      {currentView === 'analyze' && (
        <aside className="qc-sidebar">
          <div className="qc-brand">
            <div className="qc-logo-orbit">
              <div className="qc-logo-core" />
            </div>
            <div className="qc-brand-text">
              <div className="qc-brand-title">TPCN GenAI Lab. · 语音质检</div>
              <div className="qc-brand-sub">AI驱动的高级客服话术评估平台</div>
            </div>
          </div>
          
          {/* 质检标准选择器 */}
          <QCStandardsManager
            selectedStandardId={selectedStandard?.id ?? null}
            onSelectStandard={setSelectedStandard}
          />

          {/* 评估标准Prompt显示面板 */}
          <PromptDisplayPanel standardId={selectedStandard?.id ?? null} />
          
          <div className="qc-intro">
            <PulseTag label={selectedStandard?.name || "ACE CX QA Culture Rubric"} />
            <p>
              {selectedStandard 
                ? `使用【${selectedStandard.name}】标准进行质检评估`
                : '基于ACE评价体系，从准确性(Accurate)、个性化(Customized)、高效性(Efficient)三大维度，对客服通话进行全面质检评估。'
              }
            </p>
          </div>
          
          {!selectedStandard && (
            <div className="qc-highlights">
              <div className="qc-highlight-item">
                <span className="qc-dot qc-dot-blue" />
                <strong>Accurate</strong> - 信息准确性、流程合规性评估
              </div>
              <div className="qc-highlight-item">
                <span className="qc-dot qc-dot-green" />
                <strong>Customized</strong> - 服务个性化、客户关怀评估
              </div>
              <div className="qc-highlight-item">
                <span className="qc-dot qc-dot-purple" />
                <strong>Efficient</strong> - 服务效率、问题解决评估
              </div>
              <div className="qc-highlight-item">
                <span className="qc-dot qc-dot-red" />
                <strong>ZTP</strong> - 零容忍政策监控
              </div>
            </div>
          )}
          
          <div className="qc-footnote">
            {selectedStandard 
              ? `来源: ${selectedStandard.source_file || '手动创建'}`
              : '基于ACE CX QA Culture Rubric评价体系，包含19项评估指标。支持实时语音分析和历史记录查询。'
            }
          </div>
        </aside>
      )}

      <main className={`qc-main ${currentView === 'records' ? 'qc-main-full' : ''}`}>
        {currentView === 'analyze' ? (
          <>
            <VoiceUploader onAnalyze={handleAnalyze} loading={loading} />
            <VoiceAnalysisPanel 
              analysisState={analysisState}
              loading={loading}
              selectedStandard={selectedStandard}
            />
          </>
        ) : (
          <RecordsOverview />
        )}
      </main>
    </div>
  );
};
